import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalculatedMetrics } from '../types';
import { DataTable } from './DataTable';
import { ArrowLeft, TrendingUp, TrendingDown, BarChart3, Target, Building2, Users, BarChart2, Clock, Timer, Activity, Calculator } from 'lucide-react';
import { formatVolume, formatMarketCap, formatPercentage } from '../utils/calculations';

interface SetupOccurrencesDetailProps {
  calculatedData: CalculatedMetrics[];
}

// CORRETTO: Funzione helper per parsare la fascia di prezzo dalla stringa con etichette numeriche
const parsePriceRange = (fasciaLabel: string): [number, number] => {
  // Gestisce le nuove etichette numeriche corrette
  if (fasciaLabel.includes('$0 - $0.5')) return [0, 0.5];
  if (fasciaLabel.includes('$0.5 - $1')) return [0.5, 1];
  if (fasciaLabel.includes('$1 - $1.5')) return [1, 1.5];
  if (fasciaLabel.includes('$1.5 - $2')) return [1.5, 2];
  if (fasciaLabel.includes('$2 - $3')) return [2, 3];
  if (fasciaLabel.includes('$3 - $4')) return [3, 4];
  if (fasciaLabel.includes('$4 - $5')) return [4, 5];
  if (fasciaLabel.includes('$5 - $7.5')) return [5, 7.5];
  if (fasciaLabel.includes('$7.5 - $10')) return [7.5, 10];
  if (fasciaLabel.includes('$10 - $25')) return [10, 25];
  if (fasciaLabel.includes('$25 - $50')) return [25, 50];
  if (fasciaLabel.includes('$50+')) return [50, Infinity];
  
  // Fallback per compatibilità con vecchie etichette (se presenti)
  if (fasciaLabel.includes('Micro Cap ($0-5)')) return [0, 5];
  if (fasciaLabel.includes('Small ($5-10)')) return [5, 10];
  if (fasciaLabel.includes('Mid ($10-20)')) return [10, 20];
  if (fasciaLabel.includes('Large ($20-50)')) return [20, 50];
  if (fasciaLabel.includes('Mega ($50+)')) return [50, Infinity];
  
  return [0, Infinity]; // Default a tutti se la fascia non è riconosciuta
};

// Funzioni helper per calcoli sicuri
const safeAverage = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
};

const parseTime = (timeValue: Date | string | number | null | undefined): number => {
  if (timeValue instanceof Date) {
    return timeValue.getHours() * 60 + timeValue.getMinutes();
  }
  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const [hours, minutes] = timeValue.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }
  return NaN;
};

const formatTime = (minutes: number): string => {
  if (!isFinite(minutes) || isNaN(minutes)) return '00:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const SetupOccurrencesDetail: React.FC<SetupOccurrencesDetailProps> = ({ calculatedData }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setupTitle, setupParameters } = location.state || {};

  const filteredOccurrences = useMemo(() => {
    if (!setupParameters || !setupParameters['Fascia Prezzo']) {
      return [];
    }
    const [minPrice, maxPrice] = parsePriceRange(setupParameters['Fascia Prezzo']);
    
    return calculatedData.filter(row => 
      row.Open >= minPrice && row.Open < maxPrice
    );
  }, [calculatedData, setupParameters]);

  // Calcola statistiche estese per il setup
  const setupStats = useMemo(() => {
    if (filteredOccurrences.length === 0) {
      return {
        totalOccurrences: 0,
        successRate: 0,
        redCloseRate: 0,
        pmhBreakRate: 0,
        avgSpike: 0,
        avgVolumeRatio: 0,
        uniqueTickers: 0,
        averageOpenPrice: 0,
        averagePmVolume: 0,
        averageGapPercent: 0,
        averageOpenToPmh: 0,
        averageMarketCap: 0,
        averageSharesOutstanding: 0,
        averageEodVolume: 0,
        averagePmhToHigh: 0,
        averageOsRotation: 0,
        average1MinHighPercent: 0,
        average5MinLowPercent: 0,
        average30MinHighPercent: 0,
        average30MinLowPercent: 0,
        average30MinVolPmVolRatio: 0,
        hodAverageTime: '00:00',
        lodAverageTime: '00:00'
      };
    }

    const redCloses = filteredOccurrences.filter(row => row['Red Close'] === 1).length;
    const pmhBreaks = filteredOccurrences.filter(row => row['HIGH > PMH'] === 'Y').length;
    const uniqueTickers = new Set(filteredOccurrences.map(row => row.Ticker)).size;

    // Estrai tutti i valori per i calcoli
    const spikeValues = filteredOccurrences.map(row => (row['Spike %'] || 0) * 100);
    const volumeRatios = filteredOccurrences.map(row => row['Vol/PM Vol Ratio'] || 0);
    const openPriceValues = filteredOccurrences.map(row => Number(row.Open) || 0);
    const pmVolumeValues = filteredOccurrences.map(row => Number(row['Pre Market Volume']) || 0);
    const gapPercentValues = filteredOccurrences.map(row => Number(row['Gap %']) || 0);
    const openToPmhValues = filteredOccurrences.map(row => Number(row['Open to PMH']) || 0);
    const marketCapValues = filteredOccurrences.map(row => Number(row['Market Cap']) || 0);
    const sharesOutstandingValues = filteredOccurrences.map(row => Number(row['Shares Outstanding']) || 0);
    const eodVolumeValues = filteredOccurrences.map(row => Number(row.Volume) || 0);
    const pmhToHighValues = filteredOccurrences.map(row => Number(row['% PMH to HIGH']) || 0);
    const osRotationValues = filteredOccurrences.map(row => Number(row['OS Rotation']) || 0);
    const oneMinHighPercentValues = filteredOccurrences.map(row => Number(row['1 Min High %']) || 0);
    const fiveMinLowPercentValues = filteredOccurrences.map(row => Number(row['5 Min Low %']) || 0);
    const thirtyMinHighPercentValues = filteredOccurrences.map(row => Number(row['30 Min High %']) || 0);
    const thirtyMinLowPercentValues = filteredOccurrences.map(row => Number(row['30 Min Low %']) || 0);
    const thirtyMinVolPmVolRatioValues = filteredOccurrences.map(row => Number(row['Vol 30m/PM Vol']) || 0);

    // Calcola tempi medi HOD e LOD
    const hodTimes = filteredOccurrences.map(row => parseTime(row['Hod Time'])).filter(time => !isNaN(time));
    const lodTimes = filteredOccurrences.map(row => parseTime(row['Lod Time'])).filter(time => !isNaN(time));

    const avgHodTime = hodTimes.length > 0 ? hodTimes.reduce((sum, time) => sum + time, 0) / hodTimes.length : 0;
    const avgLodTime = lodTimes.length > 0 ? lodTimes.reduce((sum, time) => sum + time, 0) / lodTimes.length : 0;

    return {
      totalOccurrences: filteredOccurrences.length,
      successRate: ((filteredOccurrences.length - redCloses) / filteredOccurrences.length) * 100,
      redCloseRate: (redCloses / filteredOccurrences.length) * 100,
      pmhBreakRate: (pmhBreaks / filteredOccurrences.length) * 100,
      avgSpike: safeAverage(spikeValues),
      avgVolumeRatio: safeAverage(volumeRatios),
      uniqueTickers,
      averageOpenPrice: safeAverage(openPriceValues),
      averagePmVolume: safeAverage(pmVolumeValues),
      averageGapPercent: safeAverage(gapPercentValues),
      averageOpenToPmh: safeAverage(openToPmhValues),
      averageMarketCap: safeAverage(marketCapValues),
      averageSharesOutstanding: safeAverage(sharesOutstandingValues),
      averageEodVolume: safeAverage(eodVolumeValues),
      averagePmhToHigh: safeAverage(pmhToHighValues),
      averageOsRotation: safeAverage(osRotationValues),
      average1MinHighPercent: safeAverage(oneMinHighPercentValues),
      average5MinLowPercent: safeAverage(fiveMinLowPercentValues),
      average30MinHighPercent: safeAverage(thirtyMinHighPercentValues),
      average30MinLowPercent: safeAverage(thirtyMinLowPercentValues),
      average30MinVolPmVolRatio: safeAverage(thirtyMinVolPmVolRatioValues),
      hodAverageTime: formatTime(Math.round(avgHodTime)),
      lodAverageTime: formatTime(Math.round(avgLodTime))
    };
  }, [filteredOccurrences]);

  const getSetupIcon = (title: string) => {
    switch (title) {
      case 'Long Bias':
        return <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-green-400" />;
      case 'Short Bias':
        return <TrendingDown className="h-5 w-5 sm:h-6 sm:w-6 text-red-400" />;
      default:
        return <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-400" />;
    }
  };

  const getSetupColor = (title: string) => {
    switch (title) {
      case 'Long Bias':
        return 'border-green-400/30 bg-green-400/5';
      case 'Short Bias':
        return 'border-red-400/30 bg-red-400/5';
      default:
        return 'border-blue-400/30 bg-blue-400/5';
    }
  };

  // Determina quale tasso di successo mostrare in base al bias
  const getDisplayedSuccessRate = () => {
    if (setupTitle === 'Short Bias') {
      return setupStats.redCloseRate; // Per short bias, il successo è rappresentato dalle chiusure rosse
    }
    return setupStats.successRate; // Per long bias e altri setup, il successo è rappresentato dalle chiusure verdi
  };

  const getSuccessRateColor = () => {
    if (setupTitle === 'Short Bias') {
      return 'text-red-400'; // Rosso per short bias
    }
    if (setupTitle === 'Long Bias') {
      return 'text-green-400'; // Verde per long bias
    }
    return 'text-blue-400'; // Blu per altri setup
  };

  if (!setupTitle || !setupParameters) {
    return (
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-400 hover:text-white mb-6 sm:mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Torna alla Dashboard</span>
        </button>
        <div className="text-center py-8 sm:py-12">
          <h1 className="text-xl sm:text-2xl font-bold text-white mb-4">Dettagli Setup Non Trovati</h1>
          <p className="text-sm sm:text-base text-gray-400">Impossibile caricare i dettagli del setup. Torna alla dashboard principale.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0">
        <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Torna alla Dashboard</span>
          </button>
          <div className="hidden sm:block h-6 w-px bg-gray-600"></div>
          <div className="flex items-center space-x-2 sm:space-x-3">
            {getSetupIcon(setupTitle)}
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-white">Setup: {setupTitle}</h1>
              <p className="text-xs sm:text-sm text-gray-400">Fascia Prezzo: {setupParameters['Fascia Prezzo']}</p>
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate('/backtesting')}
          className="flex items-center space-x-1 sm:space-x-2 px-3 py-2 bg-orange-600 hover:bg-orange-500 text-white rounded-lg transition-colors text-xs sm:text-sm"
        >
          <Calculator className="h-3 w-3 sm:h-4 sm:w-4" />
          <span>Backtester</span>
        </button>
      </div>

      {/* Setup Statistics Card */}
      <div className={`border rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 ${getSetupColor(setupTitle)}`}>
        <div className="flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6">
          {getSetupIcon(setupTitle)}
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-white">Statistiche del Setup</h2>
            <p className="text-xs sm:text-sm text-gray-400">Analisi dettagliata delle {setupStats.totalOccurrences} occorrenze trovate</p>
          </div>
        </div>

        {/* Metriche Principali */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-white">{setupStats.totalOccurrences}</p>
            <p className="text-xs text-gray-400">Occorrenze Totali</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-white">{setupStats.uniqueTickers}</p>
            <p className="text-xs text-gray-400">Ticker Unici</p>
          </div>
          <div className="text-center">
            <p className={`text-lg sm:text-2xl font-bold ${getSuccessRateColor()}`}>
              {getDisplayedSuccessRate().toFixed(1)}%
            </p>
            <p className="text-xs text-gray-400">
              {setupTitle === 'Short Bias' ? 'Tasso Successo Short' : 'Tasso Successo'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-red-400">{setupStats.redCloseRate.toFixed(1)}%</p>
            <p className="text-xs text-gray-400">Chiusure Rosse</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-yellow-400">{setupStats.avgSpike.toFixed(1)}%</p>
            <p className="text-xs text-gray-400">Spike Medio</p>
          </div>
          <div className="text-center">
            <p className="text-lg sm:text-2xl font-bold text-purple-400">{setupStats.avgVolumeRatio.toFixed(1)}x</p>
            <p className="text-xs text-gray-400">Volume Ratio</p>
          </div>
        </div>

        {/* Metriche Estese */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6 mb-6 sm:mb-8">
          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-blue-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Prezzo Apertura Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-blue-400">${setupStats.averageOpenPrice.toFixed(2)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <BarChart2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Volume PM Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-green-400">{formatVolume(setupStats.averagePmVolume)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-yellow-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Gap % Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-yellow-400">{formatPercentage(setupStats.averageGapPercent)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Target className="h-3 w-3 sm:h-4 sm:w-4 text-purple-400" />
              <p className="text-xs sm:text-sm font-medium text-white">% Open to PMH Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-purple-400">{formatPercentage(setupStats.averageOpenToPmh)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Building2 className="h-3 w-3 sm:h-4 sm:w-4 text-cyan-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Market Cap Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-cyan-400">{formatMarketCap(setupStats.averageMarketCap)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Users className="h-3 w-3 sm:h-4 sm:w-4 text-orange-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Azioni in Circolazione Medie</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-orange-400">{formatVolume(setupStats.averageSharesOutstanding)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <BarChart2 className="h-3 w-3 sm:h-4 sm:w-4 text-pink-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Volume EOD Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-pink-400">{formatVolume(setupStats.averageEodVolume)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-400" />
              <p className="text-xs sm:text-sm font-medium text-white">% PMH a High Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-indigo-400">{formatPercentage(setupStats.averagePmhToHigh)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Activity className="h-3 w-3 sm:h-4 sm:w-4 text-teal-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Rotazione OS Media</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-teal-400">{formatPercentage(setupStats.averageOsRotation)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-lime-400" />
              <p className="text-xs sm:text-sm font-medium text-white">1 Min High % Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-lime-400">{formatPercentage(setupStats.average1MinHighPercent)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4 text-red-400" />
              <p className="text-xs sm:text-sm font-medium text-white">5 Min Low % Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-red-400">{formatPercentage(setupStats.average5MinLowPercent)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingUp className="h-3 w-3 sm:h-4 sm:w-4 text-emerald-400" />
              <p className="text-xs sm:text-sm font-medium text-white">30 Min High % Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-emerald-400">{formatPercentage(setupStats.average30MinHighPercent)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <TrendingDown className="h-3 w-3 sm:h-4 sm:w-4 text-rose-400" />
              <p className="text-xs sm:text-sm font-medium text-white">30 Min Low % Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-rose-400">{formatPercentage(setupStats.average30MinLowPercent)}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <BarChart2 className="h-3 w-3 sm:h-4 sm:w-4 text-violet-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Vol 30 Min/Vol PM Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-violet-400">{setupStats.average30MinVolPmVolRatio.toFixed(2)}x</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Clock className="h-3 w-3 sm:h-4 sm:w-4 text-amber-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Orario HOD Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-amber-400">{setupStats.hodAverageTime}</p>
          </div>

          <div className="bg-gray-800/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-center space-x-1 sm:space-x-2 mb-2">
              <Timer className="h-3 w-3 sm:h-4 sm:w-4 text-slate-400" />
              <p className="text-xs sm:text-sm font-medium text-white">Orario LOD Medio</p>
            </div>
            <p className="text-base sm:text-xl font-bold text-slate-400">{setupStats.lodAverageTime}</p>
          </div>
        </div>

        {/* Setup Parameters */}
        <div className="pt-4 sm:pt-6 border-t border-gray-600">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-4">Parametri del Setup</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {Object.entries(setupParameters).map(([key, value]) => (
              <div key={key} className="bg-gray-800/30 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-gray-400 mb-1">{key}</p>
                <p className={`text-xs sm:text-sm font-medium ${
                  key === 'Bias Trading' 
                    ? value === 'LONG' ? 'text-green-400' : value === 'SHORT' ? 'text-red-400' : 'text-white'
                    : 'text-white'
                }`}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-white">Occorrenze del Setup</h3>
              <p className="text-xs sm:text-sm text-gray-400 mt-1">
                Tutti i gap che rientrano nei parametri del setup "{setupTitle}" - {setupStats.totalOccurrences} occorrenze trovate
              </p>
            </div>
          </div>
        </div>
        <DataTable data={filteredOccurrences} />
      </div>
    </div>
  );
};